import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import api from "../api/axios";
import AnimatedTable from "../components/AnimatedTable";
import LoadingSpinner from "../components/LoadingSpinner";
import PageTransition from "../components/PageTransition";
import { LineChart, BarChart, DonutChart } from "../components/SimpleChart";
import { smoothTransitions } from "../utils/smoothTransitions";
import { useOptimizedAnimation } from "../hooks/useOptimizedAnimation";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";

export default function Analytics() {
  const { getOptimizedVariants, getOptimizedTransition } = useOptimizedAnimation();
  const { isDark } = useTheme();
  const { t, language } = useLanguage();

  const [analyticsData, setAnalyticsData] = useState({
    dashboard: {
      total_properties: 0,
      occupancy_rate: 0,
      total_bookings: 0,
      total_revenue: 0,
      arpu: 0,
      ltv: 0,
      churn_rate: 0,
      conversion_rate: 0
    },
    propertyGrowth: [],
    revenueData: [],
    topProperties: [],
    kpis: {},
    trends: {},
    forecasts: {},
    segments: {}
  });
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('30days');

  // Helper function for safe currency formatting
  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    if (isNaN(num) || !isFinite(num)) return 'Rp 0';
    return `Rp ${Math.round(num).toLocaleString('id-ID')}`;
  };

  // Multi-language content
  const getContent = () => {
    const lang = language || 'id';

    const content = {
      id: {
        title: 'Analytics & Laporan',
        subtitle: '30 Hari Terakhir',
        export: 'Ekspor',
        totalRevenue: 'Total Pendapatan',
        arpu: 'Pendapatan per Pengguna',
        customerLtv: 'Nilai Seumur Hidup',
        conversionRate: 'Tingkat Konversi',
        marketShare: 'Pangsa Pasar',
        properties: 'Properti',
        totalBookings: 'Total Booking',
        occupancyRate: 'Tingkat Hunian',
        propertyGrowth: 'Pertumbuhan Properti',
        revenueTrend: 'Tren Pendapatan',
        executiveSummary: 'Ringkasan Eksekutif',
        healthyGrowth: '✓ Pertumbuhan Sehat',
        revenueGrowth: 'Pertumbuhan Pendapatan',
        activeUsers: 'Pengguna Aktif',
        marketPosition: 'Posisi Pasar',
        customerSegments: 'Segmen Pelanggan',
        premium: 'Premium',
        regular: 'Reguler',
        occasional: 'Sesekali',
        users: 'pengguna',
        revenue: 'Pendapatan',
        kpiTitle: 'Indikator Kinerja Utama',
        customerRetention: 'Retensi Pelanggan',
        churnRate: 'Tingkat Churn',
        nps: 'Net Promoter Score',
        avgRevenueProperty: 'Rata-rata Pendapatan/Properti',
        bookingVelocity: 'Kecepatan Booking',
        aiInsights: 'Wawasan AI',
        nextMonthForecast: 'Prediksi Bulan Depan',
        bookings: 'booking',
        confidence: 'kepercayaan',
        revenueProjection: 'Proyeksi Pendapatan',
        smartRecommendation: 'Rekomendasi Cerdas',
        marketAlert: 'Peringatan Pasar',
        topProperties: 'Properti Terbaik',
        property: 'Properti',
        occupancy: 'Hunian',
        avgRevenue: 'Rata-rata Pendapatan',
        insights: {
          strongQ4: 'Performa Q4 kuat',
          aboveIndustry: 'Di atas rata-rata industri',
          noPayingUsers: 'Belum ada pengguna berbayar',
          monthProjection: 'Proyeksi 12 bulan',
          basedArpu: 'Berdasarkan ARPU',
          seasonalAdjustment: 'Penyesuaian musiman',
          regionalLeader: 'Pemimpin regional',
          exceedingTargets: 'Melampaui target 8.3%',
          totalUsers: 'dari total pengguna',
          regionalMarket: 'Di pasar regional',
          vsTarget: '+2.1% vs target',
          industryAvg: 'Rata-rata industri: 15%',
          belowIndustry: 'Di bawah rata-rata industri (15%)',
          excellentRating: 'Rating sangat baik',
          momGrowth: '+18% MoM',
          avgTimeBook: 'Rata-rata waktu booking',
          vsCurrentMonth: '+12% vs bulan ini',
          focusPremium: 'Fokus pada properti premium',
          higherConversion: 'Konversi lebih tinggi terdeteksi',
          seasonalPeak: 'Puncak permintaan musiman',
          optimizePricing: 'Optimalkan strategi harga'
        }
      },
      en: {
        title: 'Analytics & Reports',
        subtitle: 'Last 30 Days',
        export: 'Export',
        totalRevenue: 'Total Revenue',
        arpu: 'ARPU',
        customerLtv: 'Customer LTV',
        conversionRate: 'Conversion Rate',
        marketShare: 'Market Share',
        properties: 'Properties',
        totalBookings: 'Total Bookings',
        occupancyRate: 'Occupancy Rate',
        propertyGrowth: 'Property Growth',
        revenueTrend: 'Revenue Trend',
        executiveSummary: 'Executive Summary',
        healthyGrowth: '✓ Healthy Growth',
        revenueGrowth: 'Revenue Growth',
        activeUsers: 'Active Users',
        marketPosition: 'Market Position',
        customerSegments: 'Customer Segments',
        premium: 'Premium',
        regular: 'Regular',
        occasional: 'Occasional',
        users: 'users',
        revenue: 'Revenue',
        kpiTitle: 'Key Performance Indicators',
        customerRetention: 'Customer Retention',
        churnRate: 'Churn Rate',
        nps: 'Net Promoter Score',
        avgRevenueProperty: 'Avg Revenue/Property',
        bookingVelocity: 'Booking Velocity',
        aiInsights: 'AI Insights',
        nextMonthForecast: 'Next Month Forecast',
        bookings: 'bookings',
        confidence: 'confidence',
        revenueProjection: 'Revenue Projection',
        smartRecommendation: 'Smart Recommendation',
        marketAlert: 'Market Alert',
        topProperties: 'Top Performing Properties',
        property: 'Property',
        occupancy: 'Occupancy',
        avgRevenue: 'Avg Revenue',
        insights: {
          strongQ4: 'Strong Q4 performance',
          aboveIndustry: 'Above industry avg',
          noPayingUsers: 'No paying users yet',
          monthProjection: '12-month projection',
          basedArpu: 'Based on ARPU',
          seasonalAdjustment: 'Seasonal adjustment',
          regionalLeader: 'Regional leader',
          exceedingTargets: 'Exceeding targets by 8.3%',
          totalUsers: 'out of total users',
          regionalMarket: 'In regional market',
          vsTarget: '+2.1% vs target',
          industryAvg: 'Industry avg: 15%',
          belowIndustry: 'Below industry avg (15%)',
          excellentRating: 'Excellent rating',
          momGrowth: '+18% MoM',
          avgTimeBook: 'Avg time to book',
          vsCurrentMonth: '+12% vs current month',
          focusPremium: 'Focus on premium properties',
          higherConversion: 'Higher conversion rate detected',
          seasonalPeak: 'Seasonal demand peak',
          optimizePricing: 'Optimize pricing strategy'
        }
      }
    };

    return content[lang] || content.id;
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      // Fetch real data from APIs
      const [usersRes, propertiesRes, bookingsRes, paymentsRes] = await Promise.all([
        api.get('/users'),
        api.get('/properties'),
        api.get('/bookings'),
        api.get('/payments')
      ]);

      const users = usersRes.data.data || usersRes.data || [];
      const properties = propertiesRes.data.data || propertiesRes.data || [];
      const bookings = bookingsRes.data.data || bookingsRes.data || [];
      const payments = paymentsRes.data.data || paymentsRes.data || [];

      // Debug logging
      console.log('Analytics Debug:', {
        paymentsCount: payments.length,
        totalUsers: users.length,
        totalBookings: bookings.length,
        samplePayment: payments[0],
        paymentAmounts: payments.slice(0, 5).map(p => ({ id: p.id, amount: p.amount, type: typeof p.amount })),
        totalRevenue: payments.reduce((sum, p) => {
          const amount = parseFloat(p.amount) || 0;
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0)
      });

      // Calculate comprehensive analytics with data validation
      const totalRevenue = payments.reduce((sum, payment) => {
        const amount = parseFloat(payment.amount) || 0;
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);
      const activeBookings = bookings.filter(b => b.status === 'active').length;
      const occupancyRate = properties.length > 0 ? Math.round((activeBookings / properties.length) * 100) : 0;

      // Generate advanced analytics
      const monthlyData = generateMonthlyData(bookings);
      const kpis = calculateAdvancedKPIs(properties, bookings, payments, users);
      const revenueData = generateRevenueData(payments);
      const trends = calculateTrends(bookings, payments);
      const forecasts = generateForecasts(monthlyData, revenueData);
      const segments = analyzeCustomerSegments(users, bookings, payments);

      setAnalyticsData({
        dashboard: {
          total_properties: properties.length,
          occupancy_rate: occupancyRate,
          total_bookings: bookings.length,
          total_revenue: totalRevenue,
          arpu: kpis.arpu,
          ltv: kpis.ltv,
          churn_rate: kpis.churnRate,
          conversion_rate: kpis.conversionRate
        },
        propertyGrowth: monthlyData,
        revenueData: revenueData,
        topProperties: getTopProperties(properties, bookings, payments),
        kpis: kpis,
        trends: trends,
        forecasts: forecasts,
        segments: segments
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setLoading(false);
    }
  };

  const generateMonthlyData = (bookings) => {
    // Gunakan 6 bulan terakhir secara dinamis
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (5 - i));
      const targetMonth = date.getMonth();
      const targetYear = date.getFullYear();
      const monthBookings = bookings.filter(booking => {
        if (!booking.created_at) return false;
        const d = new Date(booking.created_at);
        return d.getMonth() === targetMonth && d.getFullYear() === targetYear;
      });
      return { month: monthNames[targetMonth], count: monthBookings.length };
    });
  };

  const getTopProperties = (properties, bookings, payments) => {
    return properties.map(property => {
      const propertyBookings = bookings.filter(b => b.property_id === property.id);
      const propertyPayments = payments.filter(p =>
        propertyBookings.some(b => b.id === p.booking_id)
      );
      const revenue = propertyPayments.reduce((sum, p) => {
        const amount = parseFloat(p.amount) || 0;
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);
      const activeBookings = propertyBookings.filter(b => b.status === 'active').length;
      const occupancy = propertyBookings.length > 0 ? Math.min(Math.round((activeBookings / 1) * 100), 100) : 0;
      const avgRevenue = propertyBookings.length > 0 ? revenue / propertyBookings.length : 0;

      // Hitung growth dari data booking nyata (bulan ini vs bulan lalu)
      const now = new Date();
      const thisMonthBookings = propertyBookings.filter(b => {
        if (!b.created_at) return false;
        const d = new Date(b.created_at);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      }).length;
      const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthBookings = propertyBookings.filter(b => {
        if (!b.created_at) return false;
        const d = new Date(b.created_at);
        return d.getMonth() === lastMonthDate.getMonth() && d.getFullYear() === lastMonthDate.getFullYear();
      }).length;
      const growth = lastMonthBookings > 0 ? ((thisMonthBookings - lastMonthBookings) / lastMonthBookings * 100) : (thisMonthBookings > 0 ? 100 : 0);
      return {
        name: property.name,
        bookings: propertyBookings.length,
        revenue: revenue,
        occupancy: occupancy,
        avgRevenue: avgRevenue,
        growth: Math.round(growth * 10) / 10
      };
    }).sort((a, b) => b.revenue - a.revenue).slice(0, 12);
  };

  const calculateAdvancedKPIs = (properties, bookings, payments, users) => {
    const currentMonth = new Date().getMonth();
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;

    const currentMonthBookings = bookings.filter(b => {
      if (!b.created_at) return false;
      return new Date(b.created_at).getMonth() === currentMonth;
    }).length;

    const lastMonthBookings = bookings.filter(b => {
      if (!b.created_at) return false;
      return new Date(b.created_at).getMonth() === lastMonth;
    }).length;

    const bookingGrowth = lastMonthBookings > 0 ?
      ((currentMonthBookings - lastMonthBookings) / lastMonthBookings * 100) : 0;

    const totalRevenue = payments.reduce((sum, p) => {
      const amount = parseFloat(p.amount) || 0;
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    const avgRevenuePerBooking = bookings.length > 0 ? totalRevenue / bookings.length : 0;

    // Advanced KPIs - Fixed calculation
    // ARPU should only count users who actually made bookings
    const payingUsers = users.filter(u => {
      return bookings.some(b => b.user_id === u.id);
    });

    const arpu = payingUsers.length > 0 ? totalRevenue / payingUsers.length : 0; // Average Revenue Per User
    const ltv = arpu * 12; // Customer Lifetime Value (estimated)

    const repeatCustomers = users.filter(u => {
      const userBookings = bookings.filter(b => b.user_id === u.id);
      return userBookings.length > 1;
    }).length;

    const customerRetention = payingUsers.length > 0 ? (repeatCustomers / payingUsers.length * 100) : 0;
    const churnRate = payingUsers.length > 0 ? (100 - customerRetention) : 0;

    const conversionRate = users.length > 0 ? (bookings.length / users.length * 100) : 0;

    // Property Performance Metrics
    const avgOccupancyRate = properties.length > 0 ?
      properties.reduce((sum, prop) => {
        const propBookings = bookings.filter(b => b.property_id === prop.id);
        return sum + (propBookings.length > 0 ? 1 : 0);
      }, 0) / properties.length * 100 : 0;

    const revenuePerProperty = properties.length > 0 ? totalRevenue / properties.length : 0;

    // Debug calculations
    console.log('KPI Calculations:', {
      totalRevenue,
      totalUsers: users.length,
      payingUsers: payingUsers.length,
      arpu,
      ltv,
      avgRevenuePerBooking
    });

    return {
      bookingGrowth: Math.round(bookingGrowth * 10) / 10,
      avgRevenuePerBooking: Math.round(avgRevenuePerBooking),
      customerRetention: Math.round(customerRetention * 10) / 10,
      arpu: Math.round(arpu),
      ltv: Math.round(ltv),
      churnRate: Math.round(churnRate * 10) / 10,
      conversionRate: Math.round(conversionRate * 10) / 10,
      avgOccupancyRate: Math.round(avgOccupancyRate * 10) / 10,
      revenuePerProperty: Math.round(revenuePerProperty),
      totalUsers: users.length,
      activeUsers: users.filter(u => u.status === 'active').length,
      repeatCustomers: repeatCustomers,
      payingUsers: payingUsers.length
    };
  };

  const calculateTrends = (bookings, payments) => {
    const last6Months = Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      return date.getMonth();
    }).reverse();

    const monthlyTrends = last6Months.map(month => {
      const monthBookings = bookings.filter(b => {
        if (!b.created_at) return false;
        return new Date(b.created_at).getMonth() === month;
      });

      const monthPayments = payments.filter(p => {
        if (!p.created_at) return false;
        return new Date(p.created_at).getMonth() === month;
      });

      return {
        month: month,
        bookings: monthBookings.length,
        revenue: monthPayments.reduce((sum, p) => {
          const amount = parseFloat(p.amount) || 0;
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0)
      };
    });

    return {
      monthlyTrends,
      bookingTrend: calculateGrowthTrend(monthlyTrends.map(m => m.bookings)),
      revenueTrend: calculateGrowthTrend(monthlyTrends.map(m => m.revenue))
    };
  };

  const calculateGrowthTrend = (data) => {
    if (data.length < 2) return 0;
    const recent = data.slice(-3).reduce((a, b) => a + b, 0) / 3;
    const previous = data.slice(-6, -3).reduce((a, b) => a + b, 0) / 3;
    return previous > 0 ? ((recent - previous) / previous * 100) : 0;
  };

  const generateForecasts = (monthlyData, revenueData) => {
    const totalBookings = monthlyData.reduce((sum, m) => sum + m.count, 0);
    const avgMonthlyBookings = monthlyData.length > 0 ? totalBookings / monthlyData.length : 0;

    const totalRevenue = revenueData.reduce((sum, r) => sum + r.amount, 0);
    const avgMonthlyRevenue = revenueData.length > 0 ? totalRevenue / revenueData.length : 0;

    // More conservative forecasting based on current data
    const nextMonthBookings = Math.max(1, Math.round(avgMonthlyBookings * 1.1));
    const projectedRevenue = Math.max(avgMonthlyRevenue, avgMonthlyRevenue * 1.05);

    return {
      nextMonthBookings: nextMonthBookings,
      projectedRevenue: Math.round(projectedRevenue),
      confidence: totalBookings > 0 ? 85 : 60 // Lower confidence if no historical data
    };
  };

  const analyzeCustomerSegments = (users, bookings, payments) => {
    const segments = {
      premium: { count: 0, revenue: 0 },
      regular: { count: 0, revenue: 0 },
      occasional: { count: 0, revenue: 0 }
    };

    users.forEach(user => {
      const userBookings = bookings.filter(b => b.user_id === user.id);
      const userPayments = payments.filter(p =>
        userBookings.some(b => b.id === p.booking_id)
      );
      const userRevenue = userPayments.reduce((sum, p) => {
        const amount = parseFloat(p.amount) || 0;
        return sum + (isNaN(amount) ? 0 : amount);
      }, 0);

      // Only categorize users who have made bookings
      if (userBookings.length > 0) {
        if (userBookings.length >= 3 || userRevenue > 5000000) {
          segments.premium.count++;
          segments.premium.revenue += userRevenue;
        } else if (userBookings.length >= 2 || userRevenue > 2000000) {
          segments.regular.count++;
          segments.regular.revenue += userRevenue;
        } else {
          segments.occasional.count++;
          segments.occasional.revenue += userRevenue;
        }
      }
    });

    return segments;
  };

  const generateRevenueData = (payments) => {
    // Gunakan 6 bulan terakhir secara dinamis
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
    return Array.from({ length: 6 }, (_, i) => {
      const date = new Date();
      date.setMonth(date.getMonth() - (5 - i));
      const targetMonth = date.getMonth();
      const targetYear = date.getFullYear();
      const monthPayments = payments.filter(payment => {
        if (!payment.created_at) return false;
        const d = new Date(payment.created_at);
        return d.getMonth() === targetMonth && d.getFullYear() === targetYear;
      });
      const amount = monthPayments.reduce((sum, p) => {
        const amt = parseFloat(p.amount) || 0;
        return sum + (isNaN(amt) ? 0 : amt);
      }, 0);
      return { month: monthNames[targetMonth], amount };
    });
  };

  const exportData = () => {
    const csvData = [
      ['Metric', 'Value'],
      ['Total Properties', analyticsData.dashboard.total_properties],
      ['Occupancy Rate', `${analyticsData.dashboard.occupancy_rate}%`],
      ['Total Bookings', analyticsData.dashboard.total_bookings],
      ['Total Revenue', formatCurrency(analyticsData.dashboard.total_revenue)]
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'analytics-report.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };



  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-900' : ''}`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <LoadingSpinner size="lg" type="building" />
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className={`text-lg mt-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
          >
            {t('loading_analytics')}
          </motion.p>
        </motion.div>
      </div>
    );
  }

  return (
    <PageTransition>
      <motion.div
        variants={getOptimizedVariants(smoothTransitions.pageVariants)}
        initial="initial"
        animate="animate"
        exit="exit"
        className="space-y-6"
      >
        {/* Header */}
        <motion.div
          variants={getOptimizedVariants(smoothTransitions.cardVariants)}
          initial="initial"
          animate="animate"
          whileHover="hover"
          className={`${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'} backdrop-blur-xl rounded-2xl shadow-xl border p-6 hover:shadow-2xl transition-all duration-300`}
        >
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <h1 className={`text-3xl font-bold ${isDark ? 'text-gray-100' : 'bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent'}`}>{getContent().title}</h1>
              <p className={`mt-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{getContent().subtitle}</p>
            </motion.div>
            <div className="flex items-center gap-3">
              <motion.select
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className={`px-4 py-3 border rounded-xl focus:ring-2 transition-all ${isDark ? 'border-gray-600 bg-gray-700 text-white focus:ring-gray-500' : 'border-gray-300 bg-white focus:ring-blue-500'}`}
              >
                <option value="7days">{t('last_7_days')}</option>
                <option value="30days">{t('last_30_days')}</option>
                <option value="90days">{t('last_90_days')}</option>
                <option value="1year">{t('last_year')}</option>
              </motion.select>
              <motion.button
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                onClick={exportData}
                className={`px-6 py-3 border rounded-xl focus:ring-2 transition-all hover:shadow-lg flex items-center gap-2 ${isDark ? 'border-gray-600 bg-gray-700 text-white hover:bg-gray-600 focus:ring-gray-500' : 'border-gray-300 bg-white hover:bg-gray-50 focus:ring-blue-500'}`}
              >
                <i className="fas fa-download"></i>
                {getContent().export}
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Key Metrics Dashboard */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            {
              icon: 'fa-building',
              title: t('total_properties_label'),
              value: analyticsData.dashboard.total_properties,
              subtitle: t('units_available'),
              color: 'blue'
            },
            {
              icon: 'fa-calendar-check',
              title: t('total_bookings_label'),
              value: analyticsData.dashboard.total_bookings,
              subtitle: `${analyticsData.kpis?.bookingGrowth?.toFixed(1) || 0}% ${t('from_last_month')}`,
              color: 'green'
            },
            {
              icon: 'fa-chart-pie',
              title: t('occupancy_rate_label'),
              value: `${analyticsData.dashboard.occupancy_rate}%`,
              subtitle: t('avg_occupancy_label'),
              color: 'purple'
            },
            {
              icon: 'fa-money-bill-wave',
              title: t('total_revenue_label'),
              value: formatCurrency(analyticsData.dashboard.total_revenue),
              subtitle: `${analyticsData.trends?.revenueTrend > 0 ? '+' : ''}${analyticsData.trends?.revenueTrend?.toFixed(1) || 0}% ${t('growth')}`,
              color: 'emerald'
            }
          ].map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6 hover:shadow-lg transition-shadow`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{metric.title}</p>
                  <p className={`text-2xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{metric.value}</p>
                  <p className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{metric.subtitle}</p>
                </div>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : `bg-${metric.color}-100`}`}>
                  <i className={`fas ${metric.icon} text-xl ${isDark ? 'text-gray-400' : `text-${metric.color}-600`}`}></i>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Revenue & Performance Metrics */}
        <motion.div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className={`lg:col-span-2 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`}
          >
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('revenue_per_property')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`}>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('avg_per_property')}</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(analyticsData.kpis?.revenuePerProperty || 0)}</p>
              </div>
              <div className={`p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`}>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('active_users')}</p>
                <p className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{analyticsData.kpis?.activeUsers || 0}</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`}
          >
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('booking_statistics')}</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('conversion_rate_label')}</span>
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{analyticsData.dashboard.conversion_rate?.toFixed(1) || 0}%</span>
                </div>
                <div className={`w-full rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div className={`h-2 rounded-full ${isDark ? 'bg-gray-500' : 'bg-blue-500'}`} style={{ width: `${analyticsData.dashboard.conversion_rate || 0}%` }}></div>
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('customer_retention')}</span>
                  <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{analyticsData.kpis?.customerRetention?.toFixed(1) || 0}%</span>
                </div>
                <div className={`w-full rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div className={`h-2 rounded-full ${isDark ? 'bg-gray-500' : 'bg-green-500'}`} style={{ width: `${analyticsData.kpis?.customerRetention || 0}%` }}></div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Booking & Revenue Trends */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`}
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('booking_revenue_trends')}</h2>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${isDark ? 'bg-gray-500' : 'bg-blue-500'}`}></div>
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('booking')}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${isDark ? 'bg-gray-600' : 'bg-green-500'}`}></div>
                <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{t('revenue_label')}</span>
              </div>
            </div>
          </div>
          <div className="h-64 flex items-end justify-between gap-4">
            {analyticsData.propertyGrowth.map((data, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div className="w-full flex items-end gap-1 h-48">
                  <motion.div
                    className={`rounded-t w-1/2 ${isDark ? 'bg-gray-500' : 'bg-blue-500'}`}
                    initial={{ height: 0 }}
                    animate={{ height: `${(data.count / Math.max(...analyticsData.propertyGrowth.map(d => d.count))) * 100}%` }}
                    transition={{ delay: 0.6 + index * 0.1, duration: 0.8 }}
                  />
                  <motion.div
                    className={`rounded-t w-1/2 ${isDark ? 'bg-gray-600' : 'bg-green-500'}`}
                    initial={{ height: 0 }}
                    animate={{ height: `${(analyticsData.revenueData[index]?.amount || 0) / Math.max(...analyticsData.revenueData.map(d => d.amount)) * 100}%` }}
                    transition={{ delay: 0.8 + index * 0.1, duration: 0.8 }}
                  />
                </div>
                <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{data.month}</p>
              </div>
            ))}
          </div>
        </motion.div>


        {/* Property Performance Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`}
        >
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('performance_summary')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-blue-500'}`}>
                  <i className={`fas fa-trending-up text-sm ${isDark ? 'text-gray-400' : 'text-white'}`}></i>
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    <i className="fas fa-chart-line mr-2"></i>
                    {t('booking_growth')}
                  </p>
                  <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{analyticsData.kpis?.bookingGrowth > 0 ? '+' : ''}{analyticsData.kpis?.bookingGrowth?.toFixed(1) || 0}%</p>
                </div>
              </div>
            </div>

            <div className={`p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-green-500'}`}>
                  <i className={`fas fa-users text-sm ${isDark ? 'text-gray-400' : 'text-white'}`}></i>
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('repeat_users')}</p>
                  <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{analyticsData.kpis?.repeatCustomers || 0}</p>
                </div>
              </div>
            </div>

            <div className={`p-4 ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'} rounded-lg`}>
              <div className="flex items-center gap-3 mb-2">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-purple-500'}`}>
                  <i className={`fas fa-chart-line text-sm ${isDark ? 'text-gray-400' : 'text-white'}`}></i>
                </div>
                <div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{t('avg_occupancy')}</p>
                  <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{analyticsData.kpis?.avgOccupancyRate?.toFixed(1) || 0}%</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Top Properties Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} rounded-lg border p-6`}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{t('top_properties_label')}</h3>
            <button
              onClick={exportData}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${isDark ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
            >
              <i className="fas fa-download mr-2"></i>
              {t('export')}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('property')}</th>
                  <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('booking')}</th>
                  <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('revenue_label')}</th>
                  <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('occupancy')}</th>
                  <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('average')}</th>
                </tr>
              </thead>
              <tbody>
                {analyticsData.topProperties.slice(0, 12).map((property, index) => (
                  <tr
                    key={index}
                    className={`border-b ${isDark ? 'border-gray-700/50' : 'border-gray-100'} hover:${isDark ? 'bg-gray-700/30' : 'bg-gray-50'} transition-colors`}
                  >
                    <td className={`py-3 px-4 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : 'bg-orange-500'}`}></div>
                        <span className="font-medium">{property.name}</span>
                      </div>
                    </td>
                    <td className={`py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{property.bookings}</td>
                    <td className={`py-3 px-4 font-medium ${isDark ? 'text-gray-300' : 'text-green-600'}`}>{formatCurrency(property.revenue)}</td>
                    <td className={`py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <div className="flex items-center gap-2">
                        <div className={`w-12 h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          <div
                            className={`h-2 rounded-full ${isDark ? 'bg-gray-500' : (property.occupancy > 80 ? 'bg-green-500' : property.occupancy > 60 ? 'bg-yellow-500' : 'bg-red-500')}`}
                            style={{ width: `${Math.min(property.occupancy, 100)}%` }}
                          ></div>
                        </div>
                        <span className="text-sm">{property.occupancy}%</span>
                      </div>
                    </td>
                    <td className={`py-3 px-4 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{formatCurrency(Math.round(property.avgRevenue))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </motion.div>
    </PageTransition>
  );
}